import designTokens from '@tableau/design-tokens';
import { ButtonWidget, PopoverAlertType } from '@tableau/widgets';
import React from 'react';

export type Group = 'data' | 'discover' | 'display' | 'download' | 'watch' | 'edit' | 'none';
export type ItemType = 'button' | 'splitButton' | 'highlightedButton' | 'popoverButton';
export type Region = 'interact' | 'performance' | 'insights' | 'watch' | 'collaborate' | 'public';

export interface ToolbarLayout {
  /** Items to display on the left side of the toolbar */
  leftItems: Region[];
  /** Items to display on the right side of the toolbar */
  rightItems: Region[];
  /** Items to display in the overflow menu. This list is a subset of regions defined in leftItems and rightItems. */
  overflow: Region[];
  /** Items to always display. Must be included in the rightItems or leftItems. */
  alwaysShow: Region[];
}

export interface VizViewerToolbarProps {
  /** List of toolbar items to render in the toolbar. Order matters. */
  items: ToolbarItem[];
  /** Defines where to place the regions in the toolbar and which regions should be displayed in the overflow menu. */
  layout: ToolbarLayout;
  /** Unique identifier for the toolbar. If provided, this will be the DOM ID. */
  id?: string;
}

export interface ToolbarColorTheme {
  /** Background color for the toolbar. Consider the color contrast of this color relative to all other overlaying colors including interactive coloring. */
  ToolbarBackground: string;
  /** Button content color at rest (e.g. text, icon). */
  ButtonContentNormal: string;
  /** Button content color when disabled (e.g. text, icon). */
  ButtonContentDisabled: string;
  /** Button background color on hover. */
  ButtonBackgroundHover: string;
  /** Button background color on press. */
  ButtonBackgroundPress: string;
  /** Button background color on activated (e.g. a flyout is open). */
  ButtonBackgroundActive: string;
  /** Button border color on focus. */
  ButtonFocusIndicator: string;
  /** Split button background color on hover. */
  SplitDropdownButtonBackgroundHover: string;
  /** Split button background color on press. */
  SplitDropdownButtonBackgroundPress: string;
  /** Highlighted button background color. */
  HighlightedButtonBackground: string;
  /** Highlighted button background color when disabled. */
  HighlightedButtonBackgroundDisabled: string;
  /** Highlighted button background color on hover. */
  HighlightedButtonBackgroundHover: string;
  /** Highlighted button background color on press. */
  HighlightedButtonBackgroundPress: string;
  /** Highlighted button content color at rest (e.g. text, icon). */
  HighlightedButtonContent: string;
  /** Highlighted button content color when disabled (e.g. text, icon). */
  HighlightedButtonContentDisabled: string;
  /** Highlighted button focus indicator color. */
  HighlightedButtonFocus: string;
  /** Highlighted button inset focus indicator color. */
  HighlightedButtonFocusInset: string;
}

export interface ToolbarItem {
  /** Callback when item is activated. */
  onClick: () => void;

  /** Localized description string that is displayed in a tooltip or in the menu item. */
  description?: string;

  /** Unique identifier */
  testId: string;

  /** What group hierarchy the item belongs to. */
  group?: Group;

  /** What region of the toolbar the item should exist. */
  region?: Region;

  /** Unique identifier for choosing the icon. */
  iconId: string;

  /** Unique identifier for the toolbar item, persists through icon changes. */
  id: string;

  /** The button we choose to display for the item (e.g. button, split button, etc) */
  itemType: ItemType;

  /** Visible label associated with the button. This can be displayed on the item or in the tooltip/accessible label. */
  label: string;

  /** Whether the button is disabled. By default the button is enabled. */
  disabled?: boolean;

  /** Whether the button is in selected state. Use this property for buttons having On/Off nature. */
  selected?: boolean;

  /** Whether the label should be visible or not. */
  showLabel?: boolean;

  /** Nested list of toolbar items. If this property is provided, this current item will be displayed as a dropdown button. */
  nestedItems?: ToolbarItem[];

  /** Label for secondary top level item. e.g. the secondary (dropdown) button in SplitButton */
  secondaryLabel?: string;

  /** The props to define if your itemType is a 'popoverButton'. */
  itemPopoverProps?: ItemPopoverProps;
}

export interface ItemPopoverProps {
  /** The content rendered inside of the popover. */
  popoverContent: React.ReactChild;

  /**
   * Will create `ButtonWidget`s in the footer which are aligned and spaced in a standardized way.
   * Note: In the toolbar, footer buttons will also close the popover on click.
   */
  footerButtons?: Array<ButtonWidget['props']>;

  /** A title is required for accessiblity, even if not shown visually. */
  title: React.ReactChild;

  /**
   * Optionally adds an alert style to the popover.
   * Note: that supplying a value here will always show a title bar, even if `showTitleBar` is set to `false`.
   */
  popoverAlertType?: PopoverAlertType;

  /**
   * Whether or not to display a title bar in the popover.
   * Note: that supplying a value to alertType will always show a title bar, regardless of this value.
   */
  showTitleBar?: boolean;
}

export const DefaultColorTheme: ToolbarColorTheme = {
  ToolbarBackground: designTokens.color.elevation.surface.default.medium_alt,
  ButtonContentNormal: designTokens.color.component.content.toolbar.default.default,
  ButtonContentDisabled: designTokens.color.component.content.toolbar.default.disabled,
  ButtonBackgroundHover: designTokens.color.component.background.toolbar.default.hover,
  ButtonBackgroundPress: designTokens.color.component.background.toolbar.default.press,
  ButtonBackgroundActive: designTokens.color.component.background.toolbar.default.toggled,
  ButtonFocusIndicator: designTokens.color.mode_agnostic.default.sky80,
  SplitDropdownButtonBackgroundHover: designTokens.color.component.background.toolbar.default.hover,
  SplitDropdownButtonBackgroundPress: designTokens.color.component.background.toolbar.default.press,
  HighlightedButtonBackground: designTokens.color.component.background.cta.agnostic.default,
  HighlightedButtonBackgroundDisabled: designTokens.color.component.background.cta.agnostic.disabled,
  HighlightedButtonBackgroundHover: designTokens.color.component.background.cta.agnostic.hover,
  HighlightedButtonBackgroundPress: designTokens.color.component.background.cta.agnostic.press,
  HighlightedButtonContent: designTokens.color.component.content.cta.agnostic.default,
  HighlightedButtonContentDisabled: designTokens.color.component.content.cta.agnostic.disabled,
  HighlightedButtonFocus: designTokens.color.mode_agnostic.default.sky80,
  HighlightedButtonFocusInset: designTokens.color.component.background.cta.agnostic.active,
};

export const ToolbarColorThemeContext = React.createContext<ToolbarColorTheme>(DefaultColorTheme);
export const ToolbarColorThemeContextProvider = ToolbarColorThemeContext.Provider;
export const useToolbarColorThemeContext = (): ToolbarColorTheme => React.useContext(ToolbarColorThemeContext);
